import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, Search, Edit2, Users, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { HomeUniLoader } from '../../components/HomeUniLoader';

const ALL_ROLES = [
  'CEO','Founder','Principal','HeadTeacher','Admin','SuperAdmin',
  'Lecturer','Teacher','FinanceOfficer','HR_Officer','Registrar',
  'ProgrammeManager','HoD','Librarian','AdmissionsOfficer','Nurse','Receptionist','IT_Support',
];

const PERMISSIONS_MAP = {
  Teacher: ['view_classes', 'create_lessons', 'create_assignments', 'take_attendance', 'grade_submissions', 'view_mail', 'view_tasks'],
  Admin: ['manage_students', 'manage_staff', 'manage_classes', 'manage_finance', 'manage_settings', 'view_audit_logs', 'manage_tasks', 'view_all'],
  SuperAdmin: ['full_access'],
  FinanceOfficer: ['manage_finance', 'view_invoices', 'record_payments', 'manage_allocations', 'view_reports'],
  HR_Officer: ['manage_staff', 'manage_leave', 'view_clock_entries', 'manage_reviews'],
  Registrar: ['view_all_teachers', 'manage_observations', 'approve_lesson_plans', 'manage_monitoring', 'manage_tasks'],
  HoD: ['view_dept_teachers', 'approve_lesson_plans', 'manage_observations', 'manage_tasks'],
  Nurse: ['manage_sick_bay', 'admit_students', 'manage_medical_inventory', 'trigger_emergency'],
  Receptionist: ['manage_inventory', 'manage_procurement', 'manage_billing', 'parent_communication', 'manage_guesthouse'],
};

const TABS = ['User Roles', 'Role Permissions'];

const CAN_EDIT_ROLES = ['SuperAdmin', 'CEO'];

export default function AccessControl() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState('User Roles');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: '', roles: [] });
  const [saving, setSaving] = useState(false);

  const canEdit = CAN_EDIT_ROLES.includes(currentUser?.role);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await api.get('/auth/users');
      setUsers(r.data || []);
    } catch {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ role: u.role || '', roles: u.roles || [] });
  };

  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await api.put(`/auth/users/${editUser.user_id}`, {
        role: editForm.role,
        roles: editForm.roles,
      });
      toast.success(`Updated role for ${editUser.name}`);
      setEditUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update role');
    }
    setSaving(false);
  };

  const toggleAdditionalRole = (role) => {
    setEditForm(f => ({
      ...f,
      roles: f.roles.includes(role)
        ? f.roles.filter(r => r !== role)
        : [...f.roles, role],
    }));
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  });

  const roleBadgeClass = (role) => {
    const map = {
      SuperAdmin: 'bg-[#EEF0F8] text-[#1B2A5E] border-[#1B2A5E]/30',
      CEO: 'bg-[#F5EDD4] text-[#A07C28] border-[#C9A84C]/30',
      Founder: 'bg-[#F5EDD4] text-[#A07C28] border-[#C9A84C]/30',
      Admin: 'bg-blue-50 text-blue-700 border-blue-200',
      Registrar: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    };
    return map[role] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) return <HomeUniLoader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#EEF0F8] flex items-center justify-center">
          <Shield className="w-5 h-5 text-[#1B2A5E]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#1B2A5E]">Access Control</h1>
          <p className="text-sm text-slate-500">Manage user roles and platform permissions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t ? 'bg-[#1B2A5E] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t === 'User Roles' ? <><Users className="w-3.5 h-3.5 inline mr-1.5" />{t}</> : <><Lock className="w-3.5 h-3.5 inline mr-1.5" />{t}</>}
          </button>
        ))}
      </div>

      {/* ── Tab 1: User Roles ── */}
      {tab === 'User Roles' && (
        <Card className="rounded-xl border-slate-100">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-base">All Users</CardTitle>
              <div className="relative w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, email or role…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 h-8 text-sm"
                />
              </div>
            </div>
            {!canEdit && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                Read-only view. Only SuperAdmin and CEO can change user roles.
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs text-slate-500">
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="text-left px-5 py-3 font-medium">Email</th>
                    <th className="text-left px-5 py-3 font-medium">Primary Role</th>
                    <th className="text-left px-5 py-3 font-medium">Additional Roles</th>
                    {canEdit && <th className="text-right px-5 py-3 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map(u => (
                    <tr key={u.user_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#EEF0F8] flex items-center justify-center text-xs font-bold text-[#1B2A5E] flex-shrink-0">
                            {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{u.email}</td>
                      <td className="px-5 py-3">
                        <Badge variant="outline" className={`text-xs ${roleBadgeClass(u.role)}`}>
                          {u.role?.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(u.roles || []).filter(r => r !== u.role).map(r => (
                            <Badge key={r} variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">
                              {r.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      {canEdit && (
                        <td className="px-5 py-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-[#1B2A5E] hover:bg-[#EEF0F8]"
                            onClick={() => openEdit(u)}
                          >
                            <Edit2 className="w-3 h-3 mr-1" /> Edit Role
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={canEdit ? 5 : 4} className="text-center py-12 text-slate-400 text-sm">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Tab 2: Role Permissions ── */}
      {tab === 'Role Permissions' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Reference grid showing permissions granted per role. Read-only.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(PERMISSIONS_MAP).map(([role, perms]) => (
              <Card key={role} className="rounded-xl border-slate-100">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${roleBadgeClass(role)}`}>
                      {role.replace(/_/g, ' ')}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {perms.map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF0F8] text-[#1B2A5E] border border-[#1B2A5E]/10">
                        {p.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {/* Roles with no explicit mapping */}
            {ALL_ROLES.filter(r => !PERMISSIONS_MAP[r]).map(role => (
              <Card key={role} className="rounded-xl border-slate-100 opacity-60">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${roleBadgeClass(role)}`}>
                      {role.replace(/_/g, ' ')}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <span className="text-[10px] text-slate-400">No explicit permissions defined</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={v => { if (!v) setEditUser(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1B2A5E]" /> Edit Role — {editUser?.name}
            </DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Primary Role</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_ROLES.map(r => (
                      <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Additional Roles</Label>
                <p className="text-[10px] text-slate-400">Grant extra permissions on top of the primary role</p>
                <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                  {ALL_ROLES.filter(r => r !== editForm.role).map(r => {
                    const checked = editForm.roles.includes(r);
                    return (
                      <label
                        key={r}
                        className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-all text-xs ${
                          checked ? 'border-[#1B2A5E] bg-[#EEF0F8] text-[#1B2A5E]' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAdditionalRole(r)}
                          className="w-3 h-3 accent-[#1B2A5E]"
                        />
                        {r.replace(/_/g, ' ')}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  variant="outline"
                  className="flex-1 rounded-lg"
                  onClick={() => setEditUser(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 rounded-lg bg-[#1B2A5E] hover:bg-[#152248] text-white"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
